import { useState } from "react";

const BASE = `${process.env.PUBLIC_URL}/bilder`;

/**
 * WideOpener — Kapitelöffner (21:9), edge-to-edge. Optional heading is rendered
 * as overlay near the bottom-center of the image (per user request).
 */
export function WideOpener({ src, alt = "", folder = "01_WIDE", heading, className = "" }) {
  const [loaded, setLoaded] = useState(false);
  const isAbsolute = /^https?:\/\//.test(src);
  const imgSrc = isAbsolute ? src : `${BASE}/${folder}/${src}`;
  return (
    <div
      data-testid={`wide-opener-${(isAbsolute ? src.split("/").pop() : src).replace(/\.[^.]+$/, "")}`}
      className={`relative mx-auto my-6 w-full overflow-hidden sm:my-8 ${className}`}
      style={{ maxWidth: "1280px", aspectRatio: "21/9", backgroundColor: "transparent" }}
    >
      <img
        src={imgSrc}
        alt={alt}
        loading="lazy"
        onLoad={() => setLoaded(true)}
        className={`block h-full w-full select-none object-cover transition-opacity duration-700 ${
          loaded ? "opacity-100" : "opacity-0"
        }`}
        draggable={false}
      />
      {heading ? (
        <div
          className="pointer-events-none absolute inset-x-0 z-10"
          style={{ bottom: "6%" }}
        >
          <div className="mx-auto w-full max-w-3xl">
            <h2
              className="text-left text-white"
              data-testid={`wide-opener-heading-${src.replace(/\.[^.]+$/, "")}`}
              style={{
                fontSize: "clamp(20px, 4vw, 40px)",
                fontWeight: 600,
                lineHeight: 1.15,
                letterSpacing: "0.3px",
                textShadow:
                  "0 2px 12px rgba(0,0,0,0.75), 0 0 24px rgba(0,0,0,0.45)",
              }}
            >
              {heading}
            </h2>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * AtmoBreak — Atmosphäre (16:9) zwischen Textblöcken.
 */
export function AtmoBreak({ src, alt = "", folder = "01_WIDE", className = "" }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <div
      data-testid={`atmo-break-${src.replace(/\.[^.]+$/, "")}`}
      className={`mx-auto my-6 w-full max-w-3xl overflow-hidden rounded-sm sm:my-8 ${className}`}
      style={{ aspectRatio: "16/9", backgroundColor: "rgba(42,38,36,0.04)" }}
    >
      <img
        src={`${BASE}/${folder}/${src}`}
        alt={alt}
        loading="lazy"
        onLoad={() => setLoaded(true)}
        className={`block h-full w-full select-none object-cover transition-opacity duration-700 ${
          loaded ? "opacity-100" : "opacity-0"
        }`}
        draggable={false}
      />
    </div>
  );
}

/**
 * IconSquare — Quadratisches Begleitbild neben/in Text (300×300).
 * Per Masterliste: „Icons = links neben Text. Klein, klar, funktional."
 * (Wir nutzen die quadratischen IST-Bilder als funktionale Akzent-Icons.)
 */
export function IconSquare({ src, alt = "", folder = "04_IST", size = 96, className = "" }) {
  return (
    <img
      src={`${BASE}/${folder}/${src}`}
      alt={alt}
      loading="lazy"
      data-testid={`icon-square-${src.replace(/\.[^.]+$/, "")}`}
      className={`inline-block shrink-0 select-none rounded-sm object-cover ${className}`}
      style={{ width: size, height: size }}
      draggable={false}
    />
  );
}

/**
 * StoryTriptych — 3 quadratische Story-Bilder nebeneinander (responsive Grid).
 */
export function StoryTriptych({ images = [], folder = "05_STORY" }) {
  return (
    <div
      data-testid="story-triptych"
      className="mx-auto my-12 grid w-full max-w-3xl grid-cols-1 gap-3 sm:my-14 sm:grid-cols-3 sm:gap-4"
    >
      {images.map((src) => (
        <div
          key={src}
          className="overflow-hidden rounded-sm"
          style={{ aspectRatio: "1/1", backgroundColor: "rgba(42,38,36,0.04)" }}
        >
          <img
            src={`${BASE}/${folder}/${src}`}
            alt=""
            loading="lazy"
            className="block h-full w-full select-none object-cover"
            draggable={false}
          />
        </div>
      ))}
    </div>
  );
}

/**
 * SideBySide — Flexbox-Layout: Quadrat-Bild links, Text/Children rechts.
 * Text bleibt rechts in seiner Spalte, fließt nie unter das Bild.
 * Auf Mobile stacked das Bild über dem Text.
 */
export function SideBySide({ src, alt = "", folder = "04_IST", children, className = "" }) {
  return (
    <div
      data-testid={`side-by-side-${src.replace(/\.[^.]+$/, "")}`}
      className={`sm:flex sm:items-start sm:gap-6 ${className}`}
    >
      <figure className="mb-4 w-full sm:mb-0 sm:w-[240px] sm:shrink-0">
        <div className="overflow-hidden rounded-sm" style={{ aspectRatio: "1/1" }}>
          <img
            src={`${BASE}/${folder}/${src}`}
            alt={alt}
            loading="lazy"
            className="block h-full w-full select-none object-cover"
            draggable={false}
          />
        </div>
      </figure>
      <div className="flex-1 space-y-5">{children}</div>
    </div>
  );
}

/**
 * SquareLeft — Quadratisches Bild, links neben Text (Desktop), oben auf Mobile.
 * Top-bündig mit Textanfang (kein oberer Margin).
 */
export function SquareLeft({ src, alt = "", folder = "04_IST", size = 240, className = "" }) {
  return (
    <figure
      data-testid={`square-left-${src.replace(/\.[^.]+$/, "")}`}
      className={`mb-4 w-full sm:float-left sm:mb-4 sm:mr-6 sm:mt-0 sm:w-[240px] ${className}`}
    >
      <div className="overflow-hidden rounded-sm" style={{ aspectRatio: "1/1" }}>
        <img
          src={`${BASE}/${folder}/${src}`}
          alt={alt}
          loading="lazy"
          className="block h-full w-full select-none object-cover"
          draggable={false}
          style={{ maxHeight: size }}
        />
      </div>
    </figure>
  );
}

/**
 * PortraitFrame — Hochkant/Square Porträt mit optionalem Caption + Sub-Caption.
 */
export function PortraitFrame({ src, alt = "", folder = "04_IST", caption, subCaption, side = "right" }) {
  return (
    <figure
      data-testid="portrait-frame"
      className={`mb-4 ${
        side === "right" ? "sm:float-right sm:ml-8" : "sm:float-left sm:mr-8"
      } w-full sm:mt-0 sm:w-[280px]`}
    >
      <div className="overflow-hidden rounded-sm" style={{ aspectRatio: "1/1" }}>
        <img
          src={`${BASE}/${folder}/${src}`}
          alt={alt}
          loading="lazy"
          className="block h-full w-full select-none object-cover"
          draggable={false}
        />
      </div>
      {caption ? (
        <figcaption
          className="mt-3 text-sm"
          style={{ color: "rgba(26,26,26,0.75)" }}
        >
          <span className="block italic">{caption}</span>
          {subCaption ? (
            <span
              className="mt-0.5 block text-xs"
              style={{ color: "rgba(26,26,26,0.55)", letterSpacing: "0.3px" }}
            >
              {subCaption}
            </span>
          ) : null}
        </figcaption>
      ) : null}
    </figure>
  );
}
